import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { fetchCategories, resetActivity, setResult } from './dashboard-actions'
import { ActivityData } from './dashboard-selectors'

import { RequestStatus } from 'common/types/request-status'
import { Category, Task } from 'data/categories'

export type DashboardState = ReturnType<typeof dashboardSlice.getInitialState>

export type Activities = {
  results: number[]
  attempts: number
}

export type TaskDomain = Task & {
  activities: Activities
}

export type CategoryDomain = Omit<Category, 'tasks'> & {
  tasks: TaskDomain[]
  activities: Activities
}

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    categories: [] as CategoryDomain[],
    search: '',
    status: 'idle' as RequestStatus,
    error: null as null | string,
  },
  reducers: {
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload
    },
    setActivityFromLocalStorage(state, action: PayloadAction<ActivityData>) {
      type CategoryWithTasksMap = Omit<CategoryDomain, 'tasks' | 'title'> & {
        tasks: { [key: string]: Pick<TaskDomain, 'id' | 'activities'> }
      }

      // creating nested associative arrays for optimization
      const categoriesMap = {} as { [key: string]: CategoryWithTasksMap }

      action.payload.forEach(category => {
        const tasksMap = {} as { [key: string]: Pick<TaskDomain, 'id' | 'activities'> }

        category.tasks.forEach(task => {
          tasksMap[task.id] = task
        })

        categoriesMap[category.id] = {
          ...category,
          tasks: tasksMap,
        }
      })

      // state changing
      state.categories = state.categories.map(category => ({
        ...category,
        activities:
          category.id in categoriesMap
            ? categoriesMap[category.id].activities
            : category.activities,
        tasks: category.tasks.map(task => ({
          ...task,
          activities:
            category.id in categoriesMap[category.id].tasks
              ? categoriesMap[category.id]?.tasks[task.id].activities
              : task.activities,
        })),
      }))
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCategories.fulfilled, (state, action) => {
      state.categories = action.payload!.map(category => ({
        ...category,
        activities: {
          results: [],
          attempts: 0,
        },
        tasks: category.tasks.map(task => ({
          ...task,
          activities: {
            results: [],
            attempts: 0,
          },
        })),
      }))
    })

    builder.addCase(setResult.fulfilled, (state, action) => {
      state.categories = state.categories.map(category =>
        category.id === action.payload!.categoryId
          ? {
              ...category,
              tasks: category.tasks.map(task =>
                task.id === action.payload!.taskId
                  ? {
                      ...task,
                      activities: {
                        results: [...task.activities.results, action.payload!.result],
                        attempts: task.activities.attempts + 1,
                      },
                    }
                  : task
              ),
              activities: {
                results: [...category.activities.results, action.payload!.result],
                attempts: category.activities.attempts + 1,
              },
            }
          : category
      )
    })
    builder.addCase(resetActivity.fulfilled, state => {
      state.categories = state.categories.map(category => ({
        ...category,
        activities: {
          results: [],
          attempts: 0,
        },
        tasks: category.tasks.map(task => ({
          ...task,
          activities: {
            results: [],
            attempts: 0,
          },
        })),
      }))
    })
    builder.addMatcher(
      action => action.type.endsWith('/pending'),
      state => {
        state.status = 'loading'
        state.error = null
      }
    )
    builder.addMatcher(
      action => action.type.endsWith('/fulfilled'),
      state => {
        state.status = 'succeeded'
        state.error = null
      }
    )
    builder.addMatcher(
      action => action.type.endsWith('/rejected'),
      (state, action) => {
        state.status = 'failed'
        state.error = action.payload || action.error.message
      }
    )
  },
})

export const { setSearch, setActivityFromLocalStorage } = dashboardSlice.actions

export const dashboardReducer = dashboardSlice.reducer
